import { Controller } from "@hotwired/stimulus"
import { buildLocalizedUrl } from "../lib/locale"

// 템플릿 마법사 컨트롤러
export default class extends Controller {
  static targets = [
    "form", "step1", "step2", "step3", "step4",
    "responseTypeRadio", "responseTypeInfo", "selectedResponseType",
    "templateTypes", "templateTypeInfo", "templateTypeInfoText",
    "step1NextBtn", "step2NextBtn", "step3NextBtn",
    "contentTextarea", "messageContentSection", "additionalSettings", "contentCharCounter",
    "buttonTemplateSettings", "buttonTemplatesContainer",
    "genericTemplateSettings", "genericElementsContainer", "genericEmptyState",
    "aiChatbotSelection",
    "previewContent", "previewButtons", "buttonTemplateLivePreview", "buttonTemplatePreviewContent", "buttonTemplatePreviewButtons",
    "publicReplyPreview", "privateReplyPreview",
    "summaryName", "summaryResponseType", "summaryTemplateType", 
    "summaryButtonCount", "summaryChatbot",
    "limitationsWarning", "limitationsText",
    "templateTypeField", "buttonTemplatesField", "genericElementsField",
    "progressBar", "stepIndicator", "stepLabels", "stepButton"
  ]
  
  static values = { 
    currentStep: Number,
    selectedResponseType: String,
    selectedTemplateType: String,
    templateId: Number,
    cfPreferredVariant: String,
    cfDeliveryHash: String,
    // i18n JSON blobs (as strings) provided by the ERB component
    responseTypeInfo: String,
    templateTypeInfo: String,
    // Consolidated i18n payload
    i18n: Object,
    locale: String
  }

  // Instagram API 제약사항 상수
  INSTAGRAM_CONSTRAINTS = {
    BUTTON_TEMPLATE: {
      MAX_COUNT: 3,
      MAX_TEXT_LENGTH: 640,
      SUPPORTED_TYPES: ['web_url', 'postback']
    },
    GENERIC_TEMPLATE: {
      MAX_ELEMENTS: 1,
      MAX_TITLE_LENGTH: 80,
      MAX_SUBTITLE_LENGTH: 80
    }
  }

  // 지역화 상태 추적을 위한 로그 헬퍼
  debugLocaleLog(label, details = {}, level = 'log') {
    const payload = {
      locale: this.currentLocale || 'unknown',
      responseType: this.selectedResponseTypeValue || null,
      templateType: this.selectedTemplateTypeValue || null,
      ...details
    }

    const logFn = console[level] || console.log

    try {
      logFn.call(console, `[TemplateWizard][Locale] ${label}`, payload)
    } catch (error) {
      console.error('[TemplateWizard][Locale] Failed to log payload', { label, error })
    }
  }

  // 안전한 JSON 파서
  safeParseJson(raw, fallback) {
    if (!raw) return fallback
    try {
      return JSON.parse(raw)
    } catch (error) {
      this.debugLocaleLog('safeParseJson_failed', {
        error: error?.message,
        rawPreview: typeof raw === 'string' ? raw.slice(0, 120) : raw
      }, 'warn')
      return fallback
    }
  }

  // 현재 DOM dataset 기반으로 로케일별 문자열 캐시 갱신
  loadLocalePayloads() {
    const ds = this.element?.dataset || {}
    this.currentLocale = ds.templateWizardLocaleValue || document.documentElement.lang || 'en'

    console.log('[TemplateWizard][Locale] raw dataset', {
      dataset: ds,
      documentLang: document.documentElement?.lang
    })

    this.responseTypeInfoCache = this.safeParseJson(ds.templateWizardResponseTypeInfoValue, {})
    this.templateTypeInfoCache = this.safeParseJson(ds.templateWizardTemplateTypeInfoValue, {})
    this.i18nValue = this.safeParseJson(ds.templateWizardI18nValue, {})

    this.debugLocaleLog('loadLocalePayloads', {
      datasetLocale: ds.templateWizardLocaleValue || null,
      documentLang: document.documentElement?.lang || null,
      responseTypeInfoKeys: Object.keys(this.responseTypeInfoCache || {}),
      templateTypeInfoKeys: Object.keys(this.templateTypeInfoCache || {}),
      i18nRootKeys: Object.keys(this.i18nValue || {})
    })
  }

  // 안전한 미리보기 출력을 위한 간단한 이스케이프
  escapeHtml(str) {
    if (str == null) return ''
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  connect() {
    console.log("Template wizard controller connected")
    
    // 로케일별 문자열 캐시 초기화
    this.loadLocalePayloads()
    
    // 상태 초기화 - Turbo Drive 페이지 전환 시 안전하게 복원
    this.initializeState()
    
    // 동적 필드 데이터 복원
    this.initializeDynamicFields()
    
    // 폼 상태에서 값 복원
    this.restoreFormState()
    
    // UI 업데이트
    this.updateView()
    
    // 이벤트 바인딩
    this.bindResponseTypeRadios()
    this.setupAdditionalSettings()
    
    // Turbo 이벤트 리스너 설정
    this.setupTurboEventListeners()
    
    // 초기 카운터/프리뷰 갱신
    this.updateContentCounter()
    this.updateButtonTemplateLivePreview()

    this.debugLocaleLog('connect_complete', {
      currentStep: this.currentStepValue,
      buttonTemplateCount: Array.isArray(this.buttonTemplates) ? this.buttonTemplates.length : 0,
      genericElementCount: Array.isArray(this.genericElements) ? this.genericElements.length : 0
    })
    console.log("Template wizard initialization completed")
  }

  // i18nValue를 항상 현재 DOM data에서 재평가하여 가져오기
  getI18n(key, fallback = '') {
    if (!this.i18nValue) {
      this.loadLocalePayloads()
    }
    const obj = this.i18nValue || {}
    if (Object.prototype.hasOwnProperty.call(obj, key) && obj[key] != null) {
      const value = obj[key]
      const locale = (this.currentLocale || '').toLowerCase()
      if (typeof value === 'string' && locale.startsWith('en') && /[가-힣]/.test(value)) {
        this.debugLocaleLog('i18n_locale_mismatch', {
          key,
          sample: value.slice(0, 80)
        }, 'warn')
      } else {
        this.debugLocaleLog('i18n_hit', { key, valueType: typeof value })
      }
      return value
    }
    this.debugLocaleLog('i18n_fallback', { key, fallback }, 'warn')
    return fallback
  }

  // i18n 게터 (기존 …Value 참조와 호환)
  get selectResponseTypeMessageValue() { return this.getI18n('selectResponseTypeMessage') }
  get selectTemplateTypeMessageValue() { return this.getI18n('selectTemplateTypeMessage') }
  get aiPromptRequiredMessageValue() { return this.getI18n('aiPromptRequiredMessage') }
  get aiToneRequiredMessageValue() { return this.getI18n('aiToneRequiredMessage') }
  get saveErrorMessageValue() { return this.getI18n('saveErrorMessage') }
  get nameRequiredMessageValue() { return this.getI18n('nameRequiredMessage') }
  get contentRequiredMessageValue() { return this.getI18n('contentRequiredMessage') }
  get responseRequiredMessageValue() { return this.getI18n('responseRequiredMessage') }
  get templateTypeRequiredMessageValue() { return this.getI18n('templateTypeRequiredMessage') }
  get cardNotFoundMessageValue() { return this.getI18n('cardNotFoundMessage') }
  get selectImageMessageValue() { return this.getI18n('selectImageMessage') }
  get uploadFailedMessageValue() { return this.getI18n('uploadFailedMessage') }
  get uploadSuccessMessageValue() { return this.getI18n('uploadSuccessMessage') }
  get uploadErrorMessageValue() { return this.getI18n('uploadErrorMessage') }
  get fileInputMissingMessageValue() { return this.getI18n('fileInputMissingMessage') }
  get uploadUrlMissingMessageValue() { return this.getI18n('uploadUrlMissingMessage') }
  get buttonTemplateMaxMessageValue() { return this.getI18n('buttonTemplateMaxMessage') }
  get genericButtonsMaxMessageValue() { return this.getI18n('genericButtonsMaxMessage') }
  get genericCardsMaxMessageValue() { return this.getI18n('genericCardsMaxMessage') }
  get previewAvatarLabelValue() { return this.getI18n('previewAvatarLabel') }
  get previewEmptyContentValue() { return this.getI18n('previewEmptyContent') }
  get previewEmptyButtonsValue() { return this.getI18n('previewEmptyButtons') }
  get previewTimeJustNowValue() { return this.getI18n('previewTimeJustNow') }
  get previewDefaultLinkLabelValue() { return this.getI18n('previewDefaultLinkLabel') }
  get previewDefaultTemplateCallLabelValue() { return this.getI18n('previewDefaultTemplateCallLabel') }
  get previewDefaultButtonLabelValue() { return this.getI18n('previewDefaultButtonLabel') }
  get livePreviewLabelValue() { return this.getI18n('livePreviewLabel') }
  get previewChannelLabelValue() { return this.getI18n('previewChannelLabel') }
  get constraintsButtonHtmlValue() { return this.getI18n('constraintsButtonHtml') }
  get constraintsGenericHtmlValue() { return this.getI18n('constraintsGenericHtml') }
  get textCounterFormatValue() { return this.getI18n('textCounterFormat') }
  get genericMemoPlaceholderValue() { return this.getI18n('genericMemoPlaceholder') }
  get normalContentPlaceholderValue() { return this.getI18n('normalContentPlaceholder') }
  get genericAddCardLabelFormatValue() { return this.getI18n('genericAddCardLabelFormat') }
  get selectTemplatePlaceholderValue() { return this.getI18n('selectTemplatePlaceholder') }
  get postbackNotSupportedMessageValue() { return this.getI18n('postbackNotSupportedMessage') }
  get postbackPayloadReadonlyLabelValue() { return this.getI18n('postbackPayloadReadonlyLabel') }
  get genericTitlePlaceholderValue() { return this.getI18n('genericTitlePlaceholder') }
  get genericImageAltValue() { return this.getI18n('genericImageAlt') }
  get genericAddImagePlaceholderValue() { return this.getI18n('genericAddImagePlaceholder') }
  get genericButtonsEmptyPlaceholderValue() { return this.getI18n('genericButtonsEmptyPlaceholder') }
  get defaultActionLabelValue() { return this.getI18n('defaultActionLabel') }
  get genericCardDetailsTitleValue() { return this.getI18n('genericCardDetailsTitle') }
  get genericImageLabelValue() { return this.getI18n('genericImageLabel') }
  get subtitleLabelValue() { return this.getI18n('subtitleLabel') }
  get subtitlePlaceholderValue() { return this.getI18n('subtitlePlaceholder') }
  get subtitleHelpTextValue() { return this.getI18n('subtitleHelpText') }
  get buttonsLabelValue() { return this.getI18n('buttonsLabel') }
  get addButtonLabelValue() { return this.getI18n('addButtonLabel') }
  get noButtonsLabelValue() { return this.getI18n('noButtonsLabel') }
  get buttonIndexLabelFormatValue() { return this.getI18n('buttonIndexLabelFormat') }
  get removeButtonTitleValue() { return this.getI18n('removeButtonTitle') }
  get buttonTitleLabelValue() { return this.getI18n('buttonTitleLabel') }
  get buttonTitlePlaceholderValue() { return this.getI18n('buttonTitlePlaceholder') }
  get buttonTypeLabelValue() { return this.getI18n('buttonTypeLabel') }
  get urlFieldLabelValue() { return this.getI18n('urlFieldLabel') }
  get phoneFieldLabelValue() { return this.getI18n('phoneFieldLabel') }
  get phonePlaceholderValue() { return this.getI18n('phonePlaceholder') }
  get phoneHelpTextValue() { return this.getI18n('phoneHelpText') }
  get replyTemplateFieldLabelValue() { return this.getI18n('replyTemplateFieldLabel') }
  get titleHelpTextValue() { return this.getI18n('titleHelpText') }
  get aiSuggestionConfirmMessageValue() { return this.getI18n('aiSuggestionConfirmMessage') }
  get aiGeneratedAppliedMessageValue() { return this.getI18n('aiGeneratedAppliedMessage') }
  get aiGeneratingMessageValue() { return this.getI18n('aiGeneratingMessage') }
  get aiFallbackNoticeValue() { return this.getI18n('aiFallbackNotice') }
  get aiSuggestionExamplesValue() { const v = this.getI18n('aiSuggestionExamples'); return Array.isArray(v) ? JSON.stringify(v) : (v || '[]') }
  get aiToneModifiersValue() { const v = this.getI18n('aiToneModifiers'); return (v && typeof v === 'object') ? JSON.stringify(v) : (v || '{}') }
  get genericSuggestedCardsMessageValue() { return this.getI18n('genericSuggestedCardsMessage') }

  // 추가 설정 초기화
  setupAdditionalSettings() {
    // 동적 필드 렌더링
    if (this.buttonTemplates.length > 0) {
      this.renderButtonTemplates()
    }
    
    if (this.genericElements && this.genericElements.length > 0) {
      this.renderGenericElements()
    }
    
    // 현재 선택된 템플릿 타입에 맞는 설정 표시
    if (this.selectedTemplateTypeValue) {
      this.showSettingsForType(this.selectedTemplateTypeValue)
    }
    
    // Generic Template 카드 개수 초기화
    if (this.selectedTemplateTypeValue === 'generic_template') {
      this.updateGenericCardCount()
    }
  }

  disconnect() {
    console.log("Template wizard controller disconnected")
    
    // 이벤트 리스너 정리
    this.removeTurboEventListeners()
  }

  // 상태 초기화
  initializeState() {
    // 기본값 설정
    this.buttonTemplates = []
    this.genericElements = []
    
    // currentStepValue가 설정되지 않았으면 1로 초기화
    if (!this.currentStepValue || this.currentStepValue < 1) {
      this.currentStepValue = 1
    }
    
    console.log("Initial state - Current step:", this.currentStepValue)
  }

  // 동적 필드 데이터 복원 (Quick Replies, Button Templates)
  initializeDynamicFields() {
    try {
      // Button Templates 복원
      if (this.hasButtonTemplatesFieldTarget && this.buttonTemplatesFieldTarget.value) {
        const buttonTemplatesData = JSON.parse(this.buttonTemplatesFieldTarget.value)
        if (Array.isArray(buttonTemplatesData)) {
          this.buttonTemplates = buttonTemplatesData
          console.log("Restored Button Templates:", this.buttonTemplates.length)
        }
      }

      // Generic Elements 복원
      if (this.hasGenericElementsFieldTarget && this.genericElementsFieldTarget.value) {
        const genericElementsData = JSON.parse(this.genericElementsFieldTarget.value)
        if (Array.isArray(genericElementsData)) {
          this.genericElements = genericElementsData
          console.log("Restored Generic Elements:", this.genericElements.length)
        }
      }
    } catch (e) {
      console.error("Failed to parse dynamic fields JSON:", e)
      this.buttonTemplates = []
      this.genericElements = []
    }
  }

  // 폼 상태 복원 (라디오 버튼, 선택된 값 등)
  restoreFormState() {
    if (!this.hasFormTarget) {
      console.warn("Form target not found for state restoration")
      return
    }
    
    // 응답 타입 복원
    const responseTypeRadio = this.formTarget.querySelector('input[name="message_template[response_type]"]:checked')
    if (responseTypeRadio) {
      this.selectedResponseTypeValue = responseTypeRadio.value
      console.log("Restored response type:", this.selectedResponseTypeValue)
      this.updateSelectedResponseTypeDisplay()
      this.updateTemplateTypes()
      this.updateResponseTypeInfo()
      // 응답 타입이 선택되어 있으면 다음 단계 버튼 활성화
      this.enableNextButton("step1NextBtn")
    }
    
    // 템플릿 타입 복원
    if (this.hasTemplateTypeFieldTarget && this.templateTypeFieldTarget.value) {
      this.selectedTemplateTypeValue = this.templateTypeFieldTarget.value
      console.log("Restored template type:", this.selectedTemplateTypeValue)
      
      // 템플릿 타입 라디오 버튼 시각적 상태 복원
      const templateTypeRadio = this.formTarget.querySelector(`input[name="template_type"][value="${this.selectedTemplateTypeValue}"]`)
      if (templateTypeRadio) {
        templateTypeRadio.checked = true
        // 시각적 카드 선택 상태도 복원
        const card = templateTypeRadio.closest('.template-type-card')
        if (card) {
          // 모든 카드에서 선택 클래스 제거
          this.formTarget.querySelectorAll('.template-type-card').forEach(c => {
            c.classList.remove('ring-2', 'ring-blue-500', 'border-blue-500')
          })
          // 현재 카드에 선택 클래스 추가
          card.classList.add('ring-2', 'ring-blue-500', 'border-blue-500')
        }
      }

      // 템플릿 타입이 복원되었으면 초기 진입 시에도 다음 단계 버튼 활성화
      // (기본값이 'text'여도 사용자가 재선택하지 않아도 진행 가능해야 함)
      this.enableNextButton("step2NextBtn")
    }
    
    // 챗봇 선택 복원
    if (this.hasChatbotSelectTarget && this.chatbotSelectTarget.value) {
      console.log("Restored chatbot selection:", this.chatbotSelectTarget.value)
    }
    
    // 콘텐츠 텍스트 영역 복원
    if (this.hasContentTextareaTarget && this.contentTextareaTarget.value) {
      console.log("Restored content length:", this.contentTextareaTarget.value.length)
    }
  }

  // Turbo 이벤트 리스너 설정
  setupTurboEventListeners() {
    this.handleBeforeCache = this.handleBeforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this.handleBeforeCache)

    // 로케일 전환/렌더 이후 i18n payload 재적용 (혼합 언어 방지)
    this.handleAfterRender = this.handleAfterRender?.bind(this) || ((event) => {
      try {
        if (!this.element) return
        const previousLocale = this.currentLocale
        this.loadLocalePayloads()
        this.debugLocaleLog('afterRender_loadLocale', {
          previousLocale,
          datasetLocale: this.element?.dataset?.templateWizardLocaleValue || null
        })

        // 동적 UI를 현재 로케일 데이터로 다시 그림
        if (this.selectedResponseTypeValue) {
          this.updateSelectedResponseTypeDisplay()
          this.updateResponseTypeInfo()
          this.updateTemplateTypes()
          this.updateTemplateTypeInfo()
        } else {
          // 아직 선택 전이면 기본 뷰만 갱신
          this.updateView()
        }

        if (previousLocale && previousLocale !== this.currentLocale) {
          console.log(`Template wizard locale updated: ${previousLocale} -> ${this.currentLocale}`)
          this.debugLocaleLog('afterRender_locale_changed', {
            previousLocale,
            newLocale: this.currentLocale
          }, 'info')
        }
      } catch (e) {
        console.warn('template-wizard: failed to refresh i18n after render', e)
      }
    })
    document.addEventListener('turbo:render', this.handleAfterRender)
  }

  // Turbo 이벤트 리스너 제거
  removeTurboEventListeners() {
    if (this.handleBeforeCache) {
      document.removeEventListener("turbo:before-cache", this.handleBeforeCache)
      this.handleBeforeCache = null
    }
    if (this.handleAfterRender) {
      document.removeEventListener('turbo:render', this.handleAfterRender)
      this.handleAfterRender = null
    }
  }

  // Turbo 캐시 전 상태 저장
  handleBeforeCache() {
    console.log("Template wizard: saving state before cache")
    
    // 현재 상태를 숨겨진 필드에 저장
    this.saveCurrentState()
  }

  // 현재 상태 저장
  saveCurrentState() {
    try {
      // Button Templates 저장
      if (this.hasButtonTemplatesFieldTarget) {
        this.buttonTemplatesFieldTarget.value = JSON.stringify(this.buttonTemplates)
      }
      
      // 선택된 템플릿 타입 저장
      if (this.hasTemplateTypeFieldTarget && this.selectedTemplateTypeValue) {
        this.templateTypeFieldTarget.value = this.selectedTemplateTypeValue
      }
      
      console.log("Current state saved successfully")
    } catch (e) {
      console.error("Failed to save current state:", e)
    }
  }

  // 응답 타입 라디오 버튼 바인딩
  bindResponseTypeRadios() {
    const responseTypeRadios = this.element.querySelectorAll('input[name="message_template[response_type]"]')
    responseTypeRadios.forEach(radio => {
      radio.addEventListener('change', (event) => {
        if (event.target.checked) {
          this.selectResponseType(event)
        }
      })
    })
    
    // 초기에 선택된 라디오 버튼이 있는지 확인
    const checkedRadio = this.element.querySelector('input[name="message_template[response_type]"]:checked')
    if (checkedRadio && !this.selectedResponseTypeValue) {
      // 선택된 버튼이 있지만 값이 설정되지 않은 경우
      this.selectedResponseTypeValue = checkedRadio.value
      console.log("Initial response type found:", this.selectedResponseTypeValue)
      this.updateResponseTypeInfo()
      this.updateTemplateTypes()
      this.enableNextButton("step1NextBtn")
      this.updateSelectedResponseTypeDisplay()
    }
  }

  // 응답 타입 선택
  selectResponseType(event) {
    this.selectedResponseTypeValue = event.target.value
    console.log("Selected response type:", this.selectedResponseTypeValue)
    this.updateResponseTypeInfo()
    this.updateTemplateTypes()
    this.enableNextButton("step1NextBtn")
    this.updateSelectedResponseTypeDisplay()
    this.updatePreviewDisplay() // 미리보기도 업데이트
  }

  // 다음 단계로 이동
  nextStep() {
    if (this.currentStepValue < 3) {
      this.currentStepValue += 1
      this.updateView()
    }
  }

  // 특정 단계로 이동 (상단 네비게이션 버튼)
  jumpToStep(event) {
    const targetStep = parseInt(event.params.step, 10)
    if (!Number.isInteger(targetStep) || targetStep < 1 || targetStep > 3) {
      return
    }

    if (!this.canMoveToStep(targetStep)) {
      return
    }

    this.saveCurrentState()
    this.currentStepValue = targetStep
    this.updateView()
  }

  // 이전 단계로 이동
  prevStep() {
    if (this.currentStepValue > 1) {
      this.currentStepValue -= 1
      this.updateView()
    }
  }

  // 단계 이동 가능 여부 검사
  canMoveToStep(targetStep) {
    if (targetStep === this.currentStepValue) {
      return false
    }

    // 뒤로 가기는 언제나 허용
    if (targetStep < this.currentStepValue) {
      return true
    }

    if (!this.selectedResponseTypeValue) {
      alert(this.selectResponseTypeMessageValue)
      return false
    }

    if (targetStep >= 3 && !this.selectedTemplateTypeValue) {
      alert(this.selectTemplateTypeMessageValue)
      return false
    }

    return true
  }

  // 뷰 업데이트
  updateView() {
    // 진행률 업데이트
    this.updateProgress()
    
    // 모든 스텝 숨기기
    this.step1Target.classList.add("hidden")
    this.step2Target.classList.add("hidden")
    if (this.hasStep3Target) this.step3Target.classList.add("hidden")
    if (this.hasStep4Target) this.step4Target.classList.add("hidden")

    // 현재 스텝만 보이기
    switch(this.currentStepValue) {
      case 1:
        this.step1Target.classList.remove("hidden")
        break
      case 2:
        this.step2Target.classList.remove("hidden")
        break
      case 3:
        if (this.hasStep3Target) {
          this.step3Target.classList.remove("hidden")
          this.updateAdditionalSettings()
        }
        break
      // step 4 removed
    }
  }

  // 진행률 업데이트
  updateProgress() {
    console.log("Updating progress - Current step:", this.currentStepValue)
    
    // 진행률 바 업데이트
    if (this.hasProgressBarTarget) {
      const percentage = (this.currentStepValue / 3) * 100
      this.progressBarTarget.style.width = `${percentage}%`
      console.log("Progress bar updated to:", percentage + "%")
    }
    
    // 단계 숫자 업데이트
    if (this.hasStepIndicatorTarget) {
      this.stepIndicatorTarget.textContent = this.currentStepValue
      console.log("Step indicator updated to:", this.currentStepValue)
    }
    
    // 단계 라벨 색상 업데이트
    if (this.hasStepLabelsTarget) {
      const buttons = this.stepLabelsTarget.querySelectorAll('[data-template-wizard-step-param]')
      buttons.forEach(button => {
        const step = parseInt(button.dataset.templateWizardStepParam, 10)
        button.classList.remove('text-indigo-600', 'font-medium')

        if (Number.isInteger(step) && step <= this.currentStepValue) {
          button.classList.add('text-indigo-600', 'font-medium')
        }

        if (Number.isInteger(step) && step === this.currentStepValue) {
          button.setAttribute('aria-current', 'step')
        } else {
          button.setAttribute('aria-current', 'false')
        }
      })
      console.log("Step labels updated - Active steps:", this.currentStepValue)
    }
  }

  // 응답 타입 정보 업데이트
  updateResponseTypeInfo() {
    const responseType = this.selectedResponseTypeValue
    const infoMessages = this.responseTypeInfoCache || {}
    
    if (this.hasResponseTypeInfoTarget) {
      this.responseTypeInfoTarget.innerHTML = `
        <div class="text-sm text-blue-800">
          ${infoMessages[responseType]}
        </div>
      `
    }
    this.debugLocaleLog('updateResponseTypeInfo', {
      responseType,
      messagePreview: typeof infoMessages[responseType] === 'string'
        ? infoMessages[responseType]?.slice(0, 120)
        : infoMessages[responseType] || null
    })
  }

  // 선택된 응답 타입 표시 업데이트
  updateSelectedResponseTypeDisplay() {
    const typeNames = this.getI18n('typeNames', {})
    
    if (this.hasSelectedResponseTypeTarget) {
      this.selectedResponseTypeTarget.textContent = typeNames[this.selectedResponseTypeValue]
    }
    this.debugLocaleLog('updateSelectedResponseTypeDisplay', {
      responseType: this.selectedResponseTypeValue,
      displayText: typeNames[this.selectedResponseTypeValue] || null
    })
  }

  // 템플릿 타입 옵션 업데이트
  updateTemplateTypes() {
    const responseType = this.selectedResponseTypeValue
    const templateTypes = this.getTemplateTypesForResponseType(responseType)

    console.log('[TemplateWizard][Locale] template type data', { responseType, templateTypes })

    if (this.hasTemplateTypesTarget) {
      this.templateTypesTarget.innerHTML = ''
      
      templateTypes.forEach(typeInfo => {
        const typeCard = this.createTemplateTypeCard(typeInfo)
        this.templateTypesTarget.appendChild(typeCard)
      })
    }
    this.debugLocaleLog('updateTemplateTypes', {
      responseType,
      templateTypeCount: Array.isArray(templateTypes) ? templateTypes.length : 0,
      templateTypes: Array.isArray(templateTypes)
        ? templateTypes.map((typeInfo) => ({
            type: typeInfo?.type,
            titlePreview: typeInfo?.title ? String(typeInfo.title).slice(0, 80) : null
          }))
        : []
    })
  }

  // 응답 타입별 템플릿 타입 반환
  getTemplateTypesForResponseType(responseType) {
    const templateTypesData = this.getI18n('templateTypes', {})

    if (responseType === 'private_reply') {
      const privateTypes = templateTypesData?.private_reply
      return Array.isArray(privateTypes) ? privateTypes : []
    }
    const publicTypes = templateTypesData?.public_reply
    return Array.isArray(publicTypes) ? publicTypes : []
  }

  // 템플릿 타입 카드 생성
  createTemplateTypeCard(typeInfo) {
    const card = document.createElement('label')
    card.className = 'group cursor-pointer'
    
    let safeTitle = this.sanitizeTitle(typeInfo.title)
    let safeDescription = typeInfo.description || ''
    let safeFeatures = typeInfo.features || []
    
    // Force English text if Korean is detected and locale is en
    const currentLocale = (this.currentLocale || '').toLowerCase()
    if (currentLocale.startsWith('en')) {
      // Korean to English mapping
      const translations = {
        '템플릿 타입': 'Template type',
        '텍스트 메시지': 'Text message',
        '직접 작성하는 텍스트': 'Manually written text',
        '액션 버튼': 'Action buttons',
        '웹사이트 방문, 전화걸기 등 액션 버튼': 'Add web link or postback actions',
        '카드': 'Card',
        '이미지와 버튼이 포함된 카드': 'A card with image and buttons',
        '• 직접 작성': '• Manual content',
        '• 챗봇 연동': '• Chatbot integration (optional)',
        '• 변수 치환': '• Variable substitution',
        '• 고정 메시지': '• Fixed message',
        '• 웹사이트 방문, 문의하기에 최적': '• Best for website visits or inquiries',
        '• 최대 3개 버튼': '• Up to 3 buttons',
        '• 웹 링크, 포스트백 지원': '• Web link and postback supported',
        '• 상품/서비스 소개': '• Showcase product/service',
        '• 단일 카드': '• Single card',
        '• 이미지+텍스트+버튼': '• Image + text + buttons'
      }
      
      // Replace Korean with English
      if (translations[safeTitle]) {
        safeTitle = translations[safeTitle]
      }
      if (translations[safeDescription]) {
        safeDescription = translations[safeDescription]
      }
      safeFeatures = safeFeatures.map(f => translations[f] || f)
    }
    
    card.innerHTML = `
      <input type="radio" name="template_type" value="${typeInfo.type}" class="sr-only template-type-radio">
      <div class="relative border-2 border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-all duration-200 group-hover:shadow-md h-full flex flex-col">
        <div class="text-center mb-3">
          <div class="text-2xl mb-2">${typeInfo.icon}</div>
          <h3 class="font-semibold text-gray-900 text-sm">${safeTitle}</h3>
          <p class="text-xs text-gray-600 mt-1">${safeDescription}</p>
        </div>
        
        <div class="flex-1 space-y-1 text-xs text-gray-600">
          ${safeFeatures.map(feature => `<div>${feature}</div>`).join('')}
        </div>
        
        <div class="absolute top-2 right-2 w-4 h-4 rounded-full border-2 border-gray-300 bg-white group-has-[:checked]:border-indigo-500 group-has-[:checked]:bg-indigo-500">
          <div class="w-2 h-2 bg-white rounded-full absolute top-0.5 left-0.5 opacity-0 group-has-[:checked]:opacity-100"></div>
        </div>
      </div>
    `
    
    // 라디오 버튼에 이벤트 리스너 추가
    const radio = card.querySelector('.template-type-radio')
    if (this.selectedTemplateTypeValue && this.selectedTemplateTypeValue === typeInfo.type) {
      radio.checked = true
    }
    radio.addEventListener('change', (event) => {
      if (event.target.checked) {
        this.selectTemplateType(event)
      }
    })

    this.debugLocaleLog('createTemplateTypeCard', {
      type: typeInfo?.type,
      title: typeInfo?.title || null,
      descriptionPreview: typeInfo?.description ? String(typeInfo.description).slice(0, 120) : null
    })
    
    return card
  }
  
  // 일부 번역 문자열에 포함된 선행 이모지 제거(카드 아이콘은 별도 필드로 표시)
  sanitizeTitle(title) {
    if (!title) return ''
    try {
      return String(title).replace(/^[\u{1F4DD}\u{1F517}\u{1F3A8}]\s*/u, '')
    } catch (e) {
      // 유니코드 정규식 미지원 환경 대비: 단순 치환
      return String(title)
        .replace(/^📝\s*/, '')
        .replace(/^🔗\s*/, '')
        .replace(/^🎨\s*/, '')
    }
  }

  // 템플릿 타입 선택
  selectTemplateType(event) {
    this.selectedTemplateTypeValue = event.target.value
    if (this.hasTemplateTypeFieldTarget) {
      this.templateTypeFieldTarget.value = this.selectedTemplateTypeValue
    }
    
    // 선택된 타입에 맞는 폼 표시 (Instagram API 스펙 적용)
    this.showFormForTemplateType(this.selectedTemplateTypeValue)
    // 챗봇 선택 필수 여부 업데이트
    this.updateAdditionalSettings()
    
    this.updateTemplateTypeInfo()
    this.enableNextButton("step2NextBtn")

    this.debugLocaleLog('selectTemplateType', {
      templateType: this.selectedTemplateTypeValue
    })
    
    console.log("Selected template type:", this.selectedTemplateTypeValue)
  }

  // 템플릿 타입 정보 업데이트
  updateTemplateTypeInfo() {
    const templateType = this.selectedTemplateTypeValue
    const infoMessages = this.templateTypeInfoCache || {}
    // Use existing max buttons message for button_template if provided
    if (!infoMessages['button_template'] && this.buttonTemplateMaxMessageValue) {
      infoMessages['button_template'] = this.buttonTemplateMaxMessageValue
    }
    
    if (this.hasTemplateTypeInfoTextTarget) {
      this.templateTypeInfoTextTarget.textContent = infoMessages[templateType]
    }
    if (this.hasTemplateTypeInfoTarget) {
      this.templateTypeInfoTarget.classList.remove("hidden")
    }
    this.debugLocaleLog('updateTemplateTypeInfo', {
      templateType,
      messagePreview: typeof infoMessages[templateType] === 'string'
        ? infoMessages[templateType]?.slice(0, 120)
        : infoMessages[templateType] || null
    })
  }

  // 추가 설정 업데이트
  updateAdditionalSettings() {
    // 모든 추가 설정 숨기기
    if (this.hasButtonTemplateSettingsTarget) {
      this.buttonTemplateSettingsTarget.classList.add("hidden")
    }
    if (this.hasGenericTemplateSettingsTarget) {
      this.genericTemplateSettingsTarget.classList.add("hidden")
    }
    if (this.hasMessageContentSectionTarget) {
      this.messageContentSectionTarget.classList.remove('hidden')
    }
    if (this.hasAiChatbotSelectionTarget) {
      this.aiChatbotSelectionTarget.classList.add("hidden")
      // 기본적으로 비활성화/비필수로 전환
      const chatbotSelect = this.formTarget?.querySelector('select[name="message_template[chatbot_id]"]')
      if (chatbotSelect) {
        chatbotSelect.removeAttribute('required')
        chatbotSelect.setAttribute('disabled', 'disabled')
      }
    }
    
    // 선택된 템플릿 타입에 따라 추가 설정 표시
    const templateType = this.selectedTemplateTypeValue
    
    if (templateType === 'button_template') {
      if (this.hasButtonTemplateSettingsTarget) {
        this.buttonTemplateSettingsTarget.classList.remove("hidden")
      }
    }
    
    if (templateType === 'generic_template') {
      if (this.hasGenericTemplateSettingsTarget) {
        this.genericTemplateSettingsTarget.classList.remove("hidden")
      }
      // Hide message content section entirely for Generic Template
      if (this.hasMessageContentSectionTarget) {
        this.messageContentSectionTarget.classList.add('hidden')
      }
      // Ensure exactly one card exists, and render it immediately
      if (!Array.isArray(this.genericElements)) { this.genericElements = [] }
      if (this.genericElements.length === 0) {
        this.genericElements.push({ title: '', subtitle: '', image_url: '', buttons: [] })
        this.renderGenericElements()
        this.updateGenericElementsField()
      } else if (this.genericElements.length > 1) {
        this.genericElements = [this.genericElements[0]]
        this.renderGenericElements()
        this.updateGenericElementsField()
      }
    }
  }
  // 템플릿 타입별 폼 전환 (Instagram API 스펙 준수)
  showFormForTemplateType(templateType) {
    console.log(`Showing form for template type: ${templateType}`)
    this.debugLocaleLog('showFormForTemplateType', { templateType })
    
    // 모든 설정 숨기기
    this.hideAllSettings()
    
    // 선택된 템플릿 타입에 따라 해당 설정 표시
    this.showSettingsForType(templateType)
  }

  // 모든 설정 숨기기
  hideAllSettings() {
    if (this.hasButtonTemplateSettingsTarget) {
      this.buttonTemplateSettingsTarget.classList.add("hidden")
    }
    if (this.hasGenericTemplateSettingsTarget) {
      this.genericTemplateSettingsTarget.classList.add("hidden")
    }
    if (this.hasAiChatbotSelectionTarget) {
      this.aiChatbotSelectionTarget.classList.add("hidden")
    }
  }

  // 타입별 설정 표시
  showSettingsForType(templateType) {
    switch(templateType) {
      case 'button_template':
        if (this.hasButtonTemplateSettingsTarget) {
          this.buttonTemplateSettingsTarget.classList.remove("hidden")
        }
        this.resetContentTextareaForNormalTemplate()
        this.updateContentCounterVisibility(true)
        this.updateContentCounter()
        this.updateButtonTemplateLivePreview()
        break
      case 'generic_template':
        if (this.hasGenericTemplateSettingsTarget) {
          this.genericTemplateSettingsTarget.classList.remove("hidden")
        }
        // Generic Template의 경우 메시지 텍스트 영역 안내 변경
        this.updateContentTextareaForGenericTemplate()
        this.updateContentCounterVisibility(false)
        break
      case 'text':
      default:
        // 텍스트 타입은 추가 설정이 없음
        this.resetContentTextareaForNormalTemplate()
        this.updateContentCounterVisibility(false)
        break
    }
    
    // Instagram API 제약사항 표시
    this.showInstagramConstraints(templateType)
  }

  // 콘텐츠 입력 핸들러
  handleContentInput() {
    this.updateContentCounter()
    if (this.selectedTemplateTypeValue === 'button_template') {
      this.updateButtonTemplateLivePreview()
    }
  }

  updateContentCounterVisibility(show) {
    if (!this.hasContentCharCounterTarget) return
    this.contentCharCounterTarget.style.display = show ? 'block' : 'none'
  }

  updateContentCounter() {
    if (!this.hasContentTextareaTarget || !this.hasContentCharCounterTarget) return
    const text = this.contentTextareaTarget.value
    const max = this.INSTAGRAM_CONSTRAINTS.BUTTON_TEMPLATE.MAX_TEXT_LENGTH
    if (this.selectedTemplateTypeValue !== 'button_template') {
      this.contentCharCounterTarget.textContent = ''
      return
    }
    const len = text.length
    const ratio = len / max
    const fmt = this.textCounterFormatValue
    this.contentCharCounterTarget.textContent = fmt.replace('{len}', len).replace('{max}', max)
    this.contentCharCounterTarget.classList.remove('text-amber-600', 'text-red-600')
    if (len > max) {
      this.contentCharCounterTarget.classList.add('text-red-600')
    } else if (ratio >= 0.9) {
      this.contentCharCounterTarget.classList.add('text-amber-600')
    }
  }

  // 버튼 템플릿 실시간 프리뷰
  updateButtonTemplateLivePreview() {
    if (!(this.hasButtonTemplatePreviewContentTarget && this.hasButtonTemplatePreviewButtonsTarget)) return

    // 텍스트 미리보기(변수 샘플 치환)
    let content = this.hasContentTextareaTarget ? this.contentTextareaTarget.value : ''
    const sampleVariables = { username: 'user123', comment_text: 'How much is the product?', post_url: 'https://instagram.com/p/example', custom_message: 'Additional message' }
    Object.entries(sampleVariables).forEach(([k, v]) => {
      content = content.replace(new RegExp(`\\{${k}\\}`, 'g'), v)
    })

    // Instagram DM 스타일 전체 컨테이너 생성 - 공백 제거
    let previewHtml = '<div class="flex items-start gap-2">'

    // 프로필 아바타
    previewHtml += '<div class="flex-shrink-0">'
    previewHtml += '<div class="w-7 h-7 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">'
    const meLabel = this.previewAvatarLabelValue
    previewHtml += `<span class="text-white text-xs font-medium">${this.escapeHtml(meLabel)}</span>`
    previewHtml += '</div></div>'

    // 메시지 말풍선
    previewHtml += '<div class="flex-1 max-w-[280px]">'
    previewHtml += '<div class="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 inline-block min-w-[280px]">'

    // 메시지 텍스트
    if (content) {
      previewHtml += `<div class="text-sm text-gray-900 leading-relaxed whitespace-pre-wrap mb-2">${this.escapeHtml(content)}</div>`
    } else {
      const empty = this.previewEmptyContentValue
      previewHtml += `<div class="text-sm text-gray-400 italic mb-2">${this.escapeHtml(empty)}</div>`
    }

    // 버튼들
    if (this.buttonTemplates && this.buttonTemplates.length > 0) {
      previewHtml += '<div class="space-y-1.5">'
      this.buttonTemplates.forEach(btn => {
        if (!btn) return
        const defaultLink = this.previewDefaultLinkLabelValue
        const defaultTemplate = this.previewDefaultTemplateCallLabelValue
        const defaultBtn = this.previewDefaultButtonLabelValue
        const label = (btn.title && btn.title.trim() !== '') ? btn.title : (btn.type === 'url' || btn.type === 'web_url' ? defaultLink : (btn.type === 'template_call' ? defaultTemplate : defaultBtn))
        previewHtml += `<button class="w-full bg-gray-200 rounded-lg px-3 py-2 hover:bg-gray-300 transition-colors duration-150">`
        previewHtml += `<div class="text-sm font-medium text-gray-900 text-center">${this.escapeHtml(label)}</div>`
        previewHtml += `</button>`
      })
      previewHtml += '</div>'
    } else {
      const emptyButtons = this.previewEmptyButtonsValue
      previewHtml += '<div class="bg-gray-50 border border-dashed border-gray-300 rounded-lg px-3 py-2">'
      previewHtml += `<div class="text-sm text-gray-400 italic text-center">${this.escapeHtml(emptyButtons)}</div>`
      previewHtml += '</div>'
    }

    previewHtml += '</div>'  // 말풍선 닫기
    const justNow = this.previewTimeJustNowValue
    previewHtml += `<div class="text-xs text-gray-400 mt-1">${this.escapeHtml(justNow)}</div>`  // 시간 표시
    previewHtml += '</div></div>'  // 컨테이너 닫기

    // 기존 타깃들을 유지하면서 미리보기 업데이트
    // buttonTemplatePreviewContent 타깃에 전체 미리보기 HTML을 설정
    this.buttonTemplatePreviewContentTarget.innerHTML = previewHtml

    // buttonTemplatePreviewButtons 타깃은 비워둠 (모든 버튼이 previewHtml에 포함됨)
    this.buttonTemplatePreviewButtonsTarget.innerHTML = ''

    // 부모 컨테이너의 스타일을 Instagram DM 스타일로 조정
    const previewContainer = this.buttonTemplatePreviewContentTarget.closest('.bg-white')
    if (previewContainer) {
      previewContainer.className = 'bg-white border border-gray-200 rounded-xl p-3'
    }
  }

  // 모든 추가 설정 숨기기
  hideAllAdditionalSettings() {
    if (this.hasButtonTemplateSettingsTarget) {
      this.buttonTemplateSettingsTarget.classList.add('hidden')
    }
    if (this.hasAiChatbotSelectionTarget) {
      this.aiChatbotSelectionTarget.classList.add('hidden')
    }
  }

  // 텍스트 메시지 폼
  showTextForm() {
    // 기본 텍스트 입력만 활성화
    console.log('Showing text message form')
  }

  // AI 챗봇 폼  
  showAiChatbotForm() {
    if (this.hasAiChatbotSelectionTarget) {
      this.aiChatbotSelectionTarget.classList.remove('hidden')
    }
    console.log('Showing AI chatbot form')
  }

  // Button Template 폼 (전화걸기 버튼 제외)
  showButtonTemplateForm() {
    if (this.hasButtonTemplateSettingsTarget) {
      this.buttonTemplateSettingsTarget.classList.remove('hidden')
    }
    console.log('Showing Button Template form (Instagram API)')
  }

  // Generic Template 폼 (새로 추가)
  showGenericTemplateForm() {
    // Generic Template 설정 영역이 있다면 표시
    console.log('Showing Generic Template form (new feature)')
  }


  // Instagram API 제약사항 표시
  showInstagramConstraints(templateType) {
    let constraints = ''

    switch(templateType) {
      case 'button_template': {
        constraints = this.constraintsButtonHtmlValue || (
          `<strong>Instagram API constraints:</strong><br>` +
          `• Up to 3 buttons<br>` +
          `• Supports web link and postback actions`
        )
        break
      }
      case 'generic_template': {
        constraints = this.constraintsGenericHtmlValue || (
          `<strong>Instagram API constraints:</strong><br>` +
          `• Title up to 80 chars, subtitle up to 80 chars<br>` +
          `• Up to 3 buttons per card`
        )
        break
      }
    }
    
    if (constraints && this.hasTemplateTypeInfoTextTarget) {
      this.templateTypeInfoTextTarget.innerHTML = constraints
      if (this.hasTemplateTypeInfoTarget) {
        this.templateTypeInfoTarget.classList.remove('hidden')
      }
    }
    this.debugLocaleLog('showInstagramConstraints', {
      templateType,
      hasConstraints: Boolean(constraints),
      constraintsPreview: typeof constraints === 'string' ? constraints.slice(0, 160) : null
    })
  }

  // Button Template 추가
  addButtonTemplate() {
    if (this.buttonTemplates.length >= 3) {
      alert(this.buttonTemplateMaxMessageValue)
      return
    }
    
    const buttonTemplate = { type: 'url', title: '', url: '' }
    this.buttonTemplates.push(buttonTemplate)
    this.renderButtonTemplates()
    this.updateButtonTemplatesField()
  }

  // Button Templates 렌더링
  renderButtonTemplates() {
    if (this.hasButtonTemplatesContainerTarget) {
      this.buttonTemplatesContainerTarget.innerHTML = ''
      
      this.buttonTemplates.forEach((template, index) => {
        const templateElement = this.createButtonTemplateElement(template, index)
        this.buttonTemplatesContainerTarget.appendChild(templateElement)
      })
      this.updateButtonTemplateLivePreview()
    }
  }

  // Button Template 요소 생성
  createButtonTemplateElement(template, index) {
    const div = document.createElement('div')
    div.className = 'p-3 border border-gray-200 rounded-lg'
    const titleLen = (template.title || '').length
    const titleCounter = this.textCounterFormatValue.replace('{len}', titleLen).replace('{max}', 20)
    
    const typeOptions = `
      <option value="url" ${template.type === 'url' ? 'selected' : ''}>🌐 ${this.previewDefaultLinkLabelValue}</option>
      <option value="phone" ${template.type === 'phone' ? 'selected' : ''}>📞 ${this.defaultActionLabelValue}</option>
      <option value="template_call" ${template.type === 'template_call' ? 'selected' : ''}>📋 ${this.previewDefaultTemplateCallLabelValue}</option>
    `

    let additionalField = ''
    switch(template.type) {
      case 'url':
        additionalField = `
          <input type="url" 
                 value="${template.url || ''}" 
                 placeholder="https://example.com"
                 class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500" 
                 data-action="input->template-wizard#updateButtonTemplate"
                 data-index="${index}"
                data-field="url">
        `
        break
      case 'phone':
        additionalField = `
          <input type=\"tel\"
                 value=\"${template.display_phone_number || template.phone_number || ''}\"
                 placeholder=\"${this.escapeHtml(this.phonePlaceholderValue)}\"
                 class=\"w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500\"
                 data-action=\"input->template-wizard#updateButtonTemplate\"
                 data-index=\"${index}\"
                 data-field=\"phone_number\">
          <p class=\"mt-1 text-[11px] text-gray-400\">${this.escapeHtml(this.phoneHelpTextValue)}</p>
        `
        break
      case 'template_call':
        additionalField = `
          <select class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  data-action="change->template-wizard#updateButtonTemplate"
                  data-index="${index}"
                  data-field="template_id">
            <option value="">${this.selectTemplatePlaceholderValue}</option>
            ${this.getAvailableTemplatesOptions(template.template_id)}
          </select>
        `
        break
      case 'postback':
        // 하위 호환성을 위해 남겨둠
        additionalField = `
          <input type="text" 
                 value="${template.payload || ''}" 
                 placeholder="action_name"
                 class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500" 
                 data-action="input->template-wizard#updateButtonTemplate"
                 data-index="${index}"
                 data-field="payload">
        `
        break
    }
    
    div.innerHTML = `
      <div class="flex items-start justify-between mb-3">
        <h4 class="text-sm font-medium text-gray-700">${this.escapeHtml(this.buttonIndexLabelFormatValue.replace('{index}', String(index + 1)))}</h4>
        <button type="button" 
                class="text-red-500 hover:text-red-700 text-sm"
                data-action="click->template-wizard#removeButtonTemplate"
                data-index="${index}">
          ${this.escapeHtml(this.getI18n('removeButtonLabel', '🗑️ Remove'))}
        </button>
      </div>
      
      <div class="grid grid-cols-2 gap-3 mb-3">
        <div>
          <label class=\"block text-xs font-medium text-gray-700 mb-1\">${this.escapeHtml(this.buttonTitleLabelValue)}</label>
          <input type="text" 
                 value="${template.title}" 
                 maxlength="20"
                 class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500" 
                 data-action="input->template-wizard#updateButtonTemplate"
                 data-index="${index}"
                 data-field="title">
          <div class=\"text-[11px] text-gray-400 mt-1\">${titleCounter}</div>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-700 mb-1">${this.escapeHtml(this.buttonTypeLabelValue)}</label>
          <select class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500" 
                  data-action="change->template-wizard#updateButtonTemplateType"
                  data-index="${index}">
            ${typeOptions}
          </select>
        </div>
      </div>
      
      <div>
        <label class="block text-xs font-medium text-gray-700 mb-1">${this.getButtonFieldLabel(template.type)}</label>
        ${additionalField}
      </div>
    `
    
    return div
  }

  // Button Template 업데이트
  updateButtonTemplate(event) {
    const index = parseInt(event.target.dataset.index)
    const field = event.target.dataset.field
    
    this.buttonTemplates[index][field] = event.target.value
    this.updateButtonTemplatesField()
    // Title 길이 카운터 업데이트
    if (field === 'title') {
      const counter = event.target.nextElementSibling
      if (counter) {
        const len = (event.target.value || '').length
        const fmt20 = this.textCounterFormatValue
        counter.textContent = fmt20.replace('{len}', len).replace('{max}', 20)
        counter.classList.remove('text-amber-600', 'text-red-600')
        if (len > 20) {
          counter.classList.add('text-red-600')
        } else if (len >= 18) {
          counter.classList.add('text-amber-600')
        }
      }
    }
    if (field === 'phone_number') {
      this.buttonTemplates[index].display_phone_number = event.target.value
    }
    this.updateButtonTemplateLivePreview()
  }

  // Button Template 타입 업데이트
  updateButtonTemplateType(event) {
    const index = parseInt(event.target.dataset.index)
    const newType = event.target.value
    
    this.buttonTemplates[index].type = newType

    // 기존 필드 초기화
    delete this.buttonTemplates[index].url
    delete this.buttonTemplates[index].phone_number
    delete this.buttonTemplates[index].display_phone_number
    delete this.buttonTemplates[index].payload
    delete this.buttonTemplates[index].template_id

    // 새 타입에 맞는 기본값 설정
    if (newType === 'template_call') {
      this.buttonTemplates[index].template_id = ''
    } else if (newType === 'phone') {
      this.buttonTemplates[index].phone_number = ''
      this.buttonTemplates[index].display_phone_number = ''
    }
    
    this.renderButtonTemplates()
    this.updateButtonTemplatesField()
    this.updateButtonTemplateLivePreview()
  }

  // Button Template 제거
  removeButtonTemplate(event) {
    const index = parseInt(event.target.dataset.index)
    this.buttonTemplates.splice(index, 1)
    this.renderButtonTemplates()
    this.updateButtonTemplatesField()
    this.updateButtonTemplateLivePreview()
  }

  // Button Template 필드 업데이트
  updateButtonTemplatesField() {
    if (this.hasButtonTemplatesFieldTarget) {
      this.buttonTemplatesFieldTarget.value = JSON.stringify(this.buttonTemplates)
    }
  }

  // 버튼 필드 라벨 반환
  getButtonFieldLabel(type) {
    const labels = {
      'url': 'URL',
      'phone': 'Phone',
      'postback': 'Action payload',
      'template_call': 'Template to reply with'
    }
    return labels[type]
  }
  
  // 사용 가능한 템플릿 옵션 가져오기
  getAvailableTemplatesOptions(selectedId) {
    // 템플릿 목록을 서버에서 가져오거나 데이터 속성에서 읽기
    const templatesData = this.element.dataset.availableTemplates
    let templates = []
    
    if (templatesData) {
      try {
        templates = JSON.parse(templatesData)
      } catch (e) {
        console.error('템플릿 데이터 파싱 오류:', e)
      }
    }
    
    return templates.map(template => 
      `<option value="${template.id}" ${selectedId == template.id ? 'selected' : ''}>
        ${template.name} (${template.template_type})
      </option>`
    ).join('')
  }

  // 변수 삽입
  insertVariable(event) {
    const variable = event.target.dataset.templateWizardVariable
    if (this.hasContentTextareaTarget) {
      const textarea = this.contentTextareaTarget
      const cursorPos = textarea.selectionStart
      const textBefore = textarea.value.substring(0, cursorPos)
      const textAfter = textarea.value.substring(textarea.selectionEnd)
      
      textarea.value = textBefore + `{${variable}}` + textAfter
      textarea.focus()
      textarea.setSelectionRange(cursorPos + variable.length + 2, cursorPos + variable.length + 2)
    }
  }

  // 미리보기 업데이트
  updatePreview() {
    // 응답 방식별 미리보기 표시/숨김
    this.updatePreviewDisplay()
    
    if (this.hasContentTextareaTarget) {
    const content = this.contentTextareaTarget.value || this.previewEmptyContentValue
      const sampleVariables = {
        'username': 'user123',
        'comment_text': '제품 가격이 궁금해요',
        'post_url': 'https://instagram.com/p/example',
        'custom_message': '추가 메시지'
      }
      
      // 변수 치환
      let previewContent = content
      Object.entries(sampleVariables).forEach(([key, value]) => {
        previewContent = previewContent.replace(new RegExp(`\\{${key}\\}`, 'g'), value)
      })
      
      // 각 미리보기 타입의 content 영역 업데이트
      const previewTargets = this.targets.findAll('previewContent')
      previewTargets.forEach(target => {
        target.textContent = previewContent
      })
      
      // 버튼들 미리보기
      this.renderPreviewButtons()
    }
  }
  
  // 응답 방식별 미리보기 표시 업데이트
  updatePreviewDisplay() {
    // 모든 미리보기 숨기기
    if (this.hasPublicReplyPreviewTarget) {
      this.publicReplyPreviewTarget.classList.add('hidden')
    }
    if (this.hasPrivateReplyPreviewTarget) {
      this.privateReplyPreviewTarget.classList.add('hidden')
    }
    
    // 선택된 응답 방식에 따른 미리보기 표시
    switch(this.selectedResponseTypeValue) {
      case 'public_reply':
        if (this.hasPublicReplyPreviewTarget) {
          this.publicReplyPreviewTarget.classList.remove('hidden')
        }
        break
      case 'private_reply':
        if (this.hasPrivateReplyPreviewTarget) {
          this.privateReplyPreviewTarget.classList.remove('hidden')
        }
        break
    }
  }

  // 미리보기 버튼 렌더링
  renderPreviewButtons() {
    // 버튼이 표시될 수 있는 응답 방식인지 확인
    const showButtons = this.selectedResponseTypeValue === 'private_reply' && 
                       (this.selectedTemplateTypeValue === 'button_template')
    
    if (!showButtons) {
      // 버튼을 지원하지 않는 경우 모든 버튼 영역 숨기기
      const buttonTargets = this.targets.findAll('previewButtons')
      buttonTargets.forEach(target => {
        target.innerHTML = ''
      })
      return
    }
    
    let buttonsHtml = ''
    
    // Quick Replies 제거됨
    
    // Button Template 버튼들
    if (this.selectedTemplateTypeValue === 'button_template') {
      this.buttonTemplates.forEach(template => {
        if (template.title) {
          const icon = template.type === 'url' ? '🔗' : template.type === 'phone' ? '📞' : '⚡'
          buttonsHtml += `<div class="inline-block bg-indigo-100 text-indigo-800 text-xs px-3 py-2 rounded-md mr-1 mb-1 border border-indigo-200">${icon} ${template.title}</div>`
        }
      })
    }
    
    // 모든 미리보기 버튼 영역에 적용
    const buttonTargets = this.targets.findAll('previewButtons')
    buttonTargets.forEach(target => {
      target.innerHTML = buttonsHtml
    })
  }

  // 요약 정보 업데이트
  updateSummary() {
    if (this.hasFormTarget) {
      // 템플릿명
      const nameField = this.formTarget.querySelector('input[name="message_template[name]"]')
      if (this.hasSummaryNameTarget) {
      this.summaryNameTarget.textContent = nameField?.value
      }
      
      // 응답방식
    const responseTypeNames = {
      'public_reply': '💬',
      'private_reply': '📨'
    }
      if (this.hasSummaryResponseTypeTarget) {
      this.summaryResponseTypeTarget.textContent = responseTypeNames[this.selectedResponseTypeValue]
      }
      
      // 템플릿 타입
    const templateTypeNames = {
      'text': '📝',
      'button_template': '🔗',
      'generic_template': '🎨'
    }
      if (this.hasSummaryTemplateTypeTarget) {
      this.summaryTemplateTypeTarget.textContent = templateTypeNames[this.selectedTemplateTypeValue]
      }
      
      // 버튼 개수
      const buttonCount = this.buttonTemplates.length
      if (this.hasSummaryButtonCountTarget) {
        const singular = this.getI18n('summaryButtonCountOne', '1 button')
        const pluralTemplate = this.getI18n('summaryButtonCountOther', '%{count} buttons')
        let countLabel
        if (buttonCount === 1) {
          countLabel = singular
        } else if (typeof pluralTemplate === 'string') {
          countLabel = pluralTemplate.replace('%{count}', buttonCount)
        }
        this.summaryButtonCountTarget.textContent = countLabel || `${buttonCount}`
      }
      
      // 챗봇 연동 - ai_generated 타입인 경우만 활성
      const isChatbotType = this.selectedTemplateTypeValue === 'ai_generated' && this.hasAiChatbotSelectionTarget
      const chatbotSelected = this.formTarget.querySelector('select[name="message_template[chatbot_id]"]')?.value
      if (this.hasSummaryChatbotTarget) {
        const activeLabel = this.getI18n('summaryChatbotActive', '✅ Active')
        const inactiveLabel = this.getI18n('summaryChatbotInactive', '❌ Inactive')
        this.summaryChatbotTarget.textContent = (isChatbotType && chatbotSelected) ? activeLabel : inactiveLabel
      }
      
      // 제한사항
      this.updateLimitations()
    }
  }

  // 제한사항 업데이트
  updateLimitations() {
    if (this.hasLimitationsTextTarget) {
      let limitations = []

      if (this.selectedResponseTypeValue === 'public_reply') {
        limitations = this.getI18n('limitationsPublicReply', [])
      } else if (this.selectedResponseTypeValue === 'private_reply') {
        limitations = this.getI18n('limitationsPrivateReply', [])
      }

      const normalizedLimitations = Array.isArray(limitations) ? limitations : (limitations ? [limitations] : [])
      this.limitationsTextTarget.innerHTML = normalizedLimitations
        .map(limitation => `<div>${limitation}</div>`)
        .join('')
    }
  }

  // 다음 버튼 활성화
  enableNextButton(targetName) {
    const button = this[`${targetName}Target`]
    if (button) {
      button.disabled = false
    }
  }

  // AI 템플릿 생성
  generateAiTemplate() {
    if (!this.hasAiPromptTarget || !this.aiPromptTarget.value.trim()) {
      alert(this.aiPromptRequiredMessageValue)
      return
    }
    
    const selectedTone = this.getSelectedAiTone()
    if (!selectedTone) {
      alert(this.aiToneRequiredMessageValue)
      return
    }
    
    // AI 생성 시뮬레이션 (실제 구현 시 API 호출)
    this.simulateAiGeneration(this.aiPromptTarget.value, selectedTone)
  }
  
  // AI 템플릿 재생성
  regenerateAiTemplate() {
    if (!this.hasAiPromptTarget || !this.aiPromptTarget.value.trim()) {
      alert(this.aiPromptRequiredMessageValue)
      return
    }
    
    const selectedTone = this.getSelectedAiTone()
    if (!selectedTone) {
      alert(this.aiToneRequiredMessageValue)
      return
    }
    
    this.simulateAiGeneration(this.aiPromptTarget.value, selectedTone)
  }
  
  // 선택된 AI 톤 가져오기
  getSelectedAiTone() {
    if (!this.hasAiToneTargets) return null
    
    const selectedTone = this.aiToneTargets.find(radio => radio.checked)
    return selectedTone ? selectedTone.value : null
  }
  
  // AI 생성 (실제 API 호출)
  async simulateAiGeneration(prompt, tone) {
    // 로딩 상태 표시
    if (this.hasAiGenerationResultTarget) {
      this.aiGenerationResultTarget.classList.remove("hidden")
      if (this.hasAiGeneratedContentTarget) {
        const msg = this.aiGeneratingMessageValue || 'Generating...'
        this.aiGeneratedContentTarget.innerHTML = `<div class="animate-pulse">${this.escapeHtml(msg)}</div>`
      }
    }
    
    try {
      // 실제 AI 생성 API 호출
      const response = await this.callAiGenerationApi(prompt, tone)
      
      if (response.success) {
        if (this.hasAiGeneratedContentTarget) {
          this.aiGeneratedContentTarget.innerHTML = `<div class="whitespace-pre-line">${response.content}</div>`
        }
        
        if (response.button_templates && response.button_templates.length > 0) {
          this.buttonTemplates = response.button_templates
          this.renderButtonTemplates()
          this.updateButtonTemplatesField()
        }
      } else {
      throw new Error(response.error || this.saveErrorMessageValue)
      }
    } catch (error) {
      console.error('AI Generation Error:', error)
      
      // 오류 시 예시 템플릿으로 대체
      const templates = this.getAiTemplateExamples(prompt, tone)
      const randomTemplate = templates[Math.floor(Math.random() * templates.length)]
      
      if (this.hasAiGeneratedContentTarget) {
        this.aiGeneratedContentTarget.innerHTML = `
          <div class="whitespace-pre-line">${randomTemplate}</div>
          <div class="mt-2 text-xs text-orange-600">${this.escapeHtml(this.aiFallbackNoticeValue || 'AI service issue; showing example template.')}</div>
        `
      }
    }
  }
  
  // AI 생성 API 호출
  async callAiGenerationApi(prompt, tone) {
    const requestData = {
      prompt: prompt,
      tone: tone,
      response_type: this.selectedResponseTypeValue,
      template_type: this.selectedTemplateTypeValue
    }
    
    const response = await fetch(buildLocalizedUrl('/api/v1/ai/generate_template'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify(requestData)
    })
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }
    
    return await response.json()
  }
  
  // AI 생성 예시 템플릿들
  getAiTemplateExamples(prompt, tone) {
    let modifiers = {}
    try {
      const raw = this.aiToneModifiersValue
      const obj = JSON.parse(raw)
      if (obj && typeof obj === 'object') modifiers = obj
    } catch(_) {}
    const fallback = {
      friendly: ['Hello! 😊', 'Thanks a lot', 'Feel free to ask anytime!'],
      professional: ['Hello.', 'Thank you for your inquiry.', 'Please contact us if you have further questions.'],
      energetic: ['Hey there! ✨', 'Thanks so much!', 'Ask us anything, anytime! 💪'],
      formal: ['Greetings.', 'We appreciate your inquiry.', 'Please let us know if you need further assistance.']
    }
    const modifier = modifiers[tone] || fallback[tone] || fallback['friendly']
    
    return [
      `${modifier[0]}
${modifier[1]}

Here is guidance for your inquiry, {username}.

{custom_message}

${modifier[2]}`,
      
      `${modifier[0]}
${modifier[1]}

Topic: {comment_text}

Please choose an option below for details.

${modifier[2]}`,
      
      `${modifier[0]}
${modifier[1]}

For a quick response, select a service:

• Product info
• Pricing  
• Order support
• Other

${modifier[2]}`
    ]
  }
  
  // AI 생성 템플릿 사용
  useAiGenerated() {
    if (this.hasAiGeneratedContentTarget && this.hasContentTextareaTarget) {
      const generatedContent = this.aiGeneratedContentTarget.textContent
      this.contentTextareaTarget.value = generatedContent
      this.updatePreview()
      
      // AI 생성 결과 숨기기
      if (this.hasAiGenerationResultTarget) {
        this.aiGenerationResultTarget.classList.add("hidden")
      }
      
      alert(this.aiGeneratedAppliedMessageValue)
    }
  }
  
  // AI 생성 템플릿 수정
  editAiGenerated() {
    if (this.hasAiGeneratedContentTarget && this.hasContentTextareaTarget) {
      const generatedContent = this.aiGeneratedContentTarget.textContent
      this.contentTextareaTarget.value = generatedContent
      this.contentTextareaTarget.focus()
      
      // AI 생성 결과 숨기기
      if (this.hasAiGenerationResultTarget) {
        this.aiGenerationResultTarget.classList.add("hidden")
      }
    }
  }
  
  // 텍스트 AI 제안 (편의 기능)
  suggestTextContent() {
    if (!this.hasContentTextareaTarget) return
    
    let suggestions = []
    try {
      const raw = this.aiSuggestionExamplesValue
      const arr = JSON.parse(raw)
      if (Array.isArray(arr)) suggestions = arr
    } catch(_) {}
    if (suggestions.length === 0) {
      suggestions = [
        'Hello {username}!\nThanks for reaching out.\n\nLet me know if you have any other questions!',
        'Hi {username},\n\nRegarding {comment_text}, here is some guidance.\n\nFeel free to DM us for detailed help.',
        'Hello {username}!\n\nPlease choose an option below for a quick response.\n\nThank you!'
      ]
    }
    
    const randomSuggestion = suggestions[Math.floor(Math.random() * suggestions.length)]
    
    if (confirm(this.aiSuggestionConfirmMessageValue)) {
      this.contentTextareaTarget.value = randomSuggestion
      this.updatePreview()
    }
  }

  // 테스트 발송 기능 제거됨
  
  // 저장 제출 (안전 제출 가드)
  submitForm() {
    try {
      // 동적 상태를 숨겨진 필드에 저장
      this.saveCurrentState()
      // 선택값 기본 검증(클라이언트) – 실패 시 제출 중단
      const data = this.gatherTemplateData()
      if (!this.validateTemplateData(data)) {
        return
      }

      // 기본 브라우저 검증에 맡기고 제출 (requestSubmit 지원 안되면 submit)
      if (this.hasFormTarget) {
        console.log('Submitting template form...')
        if (typeof this.formTarget.requestSubmit === 'function') {
          this.formTarget.requestSubmit()
        } else {
          this.formTarget.submit()
        }
      } else {
        console.warn('Form target not found; cannot submit')
      }
    } catch (e) {
      console.error('폼 제출 중 오류:', e)
      alert(this.saveErrorMessageValue)
    }
  }
  
  // 템플릿 데이터 수집
  gatherTemplateData() {
    return {
      name: this.formTarget.querySelector('input[name="message_template[name]"]')?.value,
      content: this.contentTextareaTarget?.value,
      responseType: this.selectedResponseTypeValue,
      templateType: this.selectedTemplateTypeValue,
      buttonTemplates: this.buttonTemplates,
      chatbotId: this.formTarget.querySelector('select[name="message_template[chatbot_id]"]')?.value
    }
  }
  
  // 템플릿 데이터 검증
  validateTemplateData(data) {
    if (!data.name || data.name.trim() === '') {
      alert(this.nameRequiredMessageValue)
      return false
    }
    
    if (!data.content || data.content.trim() === '') {
      alert(this.contentRequiredMessageValue)
      return false
    }
    
    if (!data.responseType) {
      alert(this.responseRequiredMessageValue)
      return false
    }
    
    if (!data.templateType) {
      alert(this.templateTypeRequiredMessageValue)
      return false
    }
    
    // Instagram API 제약사항 검증
    if (!this.validateInstagramConstraints(data)) {
      return false
    }
    
    return true
  }

  // Instagram API 제약사항 검증
  validateInstagramConstraints(data) {
    // Button Template 검증
    if (data.buttonTemplates && data.buttonTemplates.length > 0) {
      if (data.buttonTemplates.length > this.INSTAGRAM_CONSTRAINTS.BUTTON_TEMPLATE.MAX_COUNT) {
        alert(this.buttonTemplateMaxMessageValue)
        return false
      }
      
      for (let i = 0; i < data.buttonTemplates.length; i++) {
        const button = data.buttonTemplates[i]
        if (!button.title || button.title.trim() === '') {
          alert(this.buttonTitleLabelValue)
          return false
        }

        if (button.type === 'web_url' && (!button.url || button.url.trim() === '')) {
          alert(this.urlFieldLabelValue)
          return false
        }

        if (button.type === 'postback' && (!button.payload || button.payload.trim() === '')) {
          alert(this.postbackPayloadReadonlyLabelValue)
          return false
        }

        if (button.type === 'phone') {
          const rawPhone = button.phone_number ? button.phone_number.trim() : ''
          if (rawPhone === '') {
            alert(this.phoneFieldLabelValue)
            return false
          }

          const digits = rawPhone.replace(/[^0-9+]/g, '')
          if (digits.length < 7) {
            alert(this.phoneFieldLabelValue)
            return false
          }
        }
      }
    }
    
    // Generic Template 검증
    if (data.templateType === 'generic_template' && this.genericElements && this.genericElements.length > 0) {
      if (this.genericElements.length > this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_ELEMENTS) {
        alert(this.genericCardsMaxMessageValue)
        return false
      }
      
      for (let i = 0; i < this.genericElements.length; i++) {
        const element = this.genericElements[i]
        
        if (!element.title || element.title.trim() === '') {
          alert(this.genericTitlePlaceholderValue)
          return false
        }
        
        if (element.title.length > this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_TITLE_LENGTH) {
          alert(this.textCounterFormatValue)
          return false
        }
        
        if (element.subtitle && element.subtitle.length > this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_SUBTITLE_LENGTH) {
          alert(this.textCounterFormatValue)
          return false
        }
        
        if (!element.image_url || element.image_url.trim() === '') {
          alert(this.genericImageAltValue)
          return false
        }
        
        // URL 형식 간단 검증
        try {
          new URL(element.image_url)
        } catch {
          alert(this.genericImageAltValue)
          return false
        }
        
        // 버튼 검증
        if (element.buttons && element.buttons.length > 3) {
          alert(this.genericButtonsMaxMessageValue)
          return false
        }

        if (element.buttons && element.buttons.length > 0) {
          for (let j = 0; j < element.buttons.length; j++) {
            const button = element.buttons[j]
            if (!button.title || button.title.trim() === '') {
              alert(this.buttonTitleLabelValue)
              return false
            }

            if (button.type === 'web_url') {
              if (!button.url || button.url.trim() === '') {
                alert(this.urlFieldLabelValue)
                return false
              }
            } else if (button.type === 'template_call') {
              if (!button.template_id || button.template_id.trim() === '') {
                alert(this.selectTemplatePlaceholderValue)
                return false
              }
            } else if (button.type === 'phone') {
              const phoneValue = button.phone_number ? button.phone_number.trim() : ''
              if (phoneValue === '') {
                alert(this.phoneFieldLabelValue)
                return false
              }

              const digits = phoneValue.replace(/[^0-9+]/g, '')
              if (digits.length < 7) {
                alert(this.phoneFieldLabelValue)
                return false
              }
            }
          }
        }
      }
    }
    
    // 메시지 텍스트 길이 검증 (Button Template인 경우)
    if (data.templateType === 'button_template' && data.content) {
      if (data.content.length > this.INSTAGRAM_CONSTRAINTS.BUTTON_TEMPLATE.MAX_TEXT_LENGTH) {
        alert(this.textCounterFormatValue)
        return false
      }
    }
    
    return true
  }

  // Generic Template용 메시지 텍스트 영역 업데이트
  updateContentTextareaForGenericTemplate() {
    if (this.hasContentTextareaTarget) {
      const ph = this.genericMemoPlaceholderValue
      this.contentTextareaTarget.placeholder = ph
      this.contentTextareaTarget.style.backgroundColor = '#f9fafb'
      this.contentTextareaTarget.style.fontStyle = 'italic'
    }
  }

  // 일반 템플릿용 메시지 텍스트 영역 복원
  resetContentTextareaForNormalTemplate() {
    if (this.hasContentTextareaTarget) {
      const ph = this.normalContentPlaceholderValue
      this.contentTextareaTarget.placeholder = ph
      this.contentTextareaTarget.style.backgroundColor = '#ffffff'
      this.contentTextareaTarget.style.fontStyle = 'normal'
    }
  }

  // Generic Template 카드 개수 업데이트
  updateGenericCardCount() {
    // 카드 추가 버튼의 텍스트 업데이트
    const addButton = this.genericTemplateSettingsTarget?.querySelector('[data-action*="addGenericElement"]')
    if (addButton) {
      const count = this.genericElements.length
      const maxCount = this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_ELEMENTS
      
      // 버튼 내의 아이콘 뒤의 텍스트를 업데이트
      const iconSpan = addButton.querySelector('span.mr-2')
      const fmt = this.genericAddCardLabelFormatValue
      const label = fmt.replace('{count}', count).replace('{max}', maxCount)
      if (iconSpan) {
        addButton.childNodes.forEach(node => {
          if (node.nodeType === Node.TEXT_NODE) { node.textContent = ' ' + label }
        })
      } else {
        addButton.textContent = label
      }
      
      // 최대 개수 도달 시 버튼 비활성화
      if (count >= maxCount) {
        addButton.disabled = true
        addButton.classList.add('opacity-50', 'cursor-not-allowed')
      } else {
        addButton.disabled = false
        addButton.classList.remove('opacity-50', 'cursor-not-allowed')
      }
    }
    
    // Empty state 표시/숨김
    if (this.hasGenericEmptyStateTarget) {
      if (this.genericElements.length === 0) {
        this.genericEmptyStateTarget.classList.remove('hidden')
      } else {
        this.genericEmptyStateTarget.classList.add('hidden')
      }
    }
  }

  // Generic Template 요소 추가
  addGenericElement() {
    if (this.genericElements.length >= this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_ELEMENTS) {
      alert(this.genericCardsMaxMessageValue || `You can add up to ${this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_ELEMENTS} cards.`)
      return
    }
    
    const newElement = {
      title: '',
      subtitle: '',
      image_url: '',
      buttons: []
    }
    
    this.genericElements.push(newElement)
    this.renderGenericElements()
    this.updateGenericElementsField()
    this.updateGenericCardCount()
  }

  // Image 업로드 → DM용 URL 주입
  async uploadGenericImage(event) {
    try {
      const index = parseInt(event.currentTarget.dataset.index)
      if (!Number.isInteger(index)) { alert(this.cardNotFoundMessageValue); return }

      // 같은 카드 영역의 파일 입력 찾기
      const container = event.currentTarget.closest('.p-4')
      const fileInput = container?.querySelector('input[type="file"][data-template-wizard-target="genericImageInput"]')
      if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
        alert(this.selectImageMessageValue)
        return
      }

      const file = fileInput.files[0]
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

      // 서버를 통한 Cloudflare 업로드만 사용 (Direct Upload 미사용)
      let finalUrl = null
      try {
        const formData = new FormData()
        formData.append('image', file, file.name || 'upload')
        const res = await fetch(buildLocalizedUrl('/uploads/images'), {
          method: 'POST',
          headers: token ? { 'X-CSRF-Token': token } : {},
          body: formData
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || !data.success) {
          const msg = data && data.error ? data.error : this.uploadFailedMessageValue
          alert(msg)
          return
        }
        finalUrl = data.url
      } catch (e) {
        alert(`${this.uploadFailedMessageValue}: ${e.message || e}`)
        return
      }

      if (!finalUrl) {
        alert(this.uploadFailedMessageValue)
        return
      }

      // URL 주입 후 필드 갱신
      this.genericElements[index].image_url = finalUrl
      this.updateGenericElementsField()
      this.renderGenericElements()
      alert(this.uploadSuccessMessageValue)
    } catch (e) {
      console.error('Image upload error:', e)
      alert(this.uploadErrorMessageValue)
    }
  }

  // Generic Template 요소 렌더링
  renderGenericElements() {
    if (this.hasGenericElementsContainerTarget) {
      this.genericElementsContainerTarget.innerHTML = ''

      this.genericElements.forEach((element, index) => {
        const elementDiv = this.createGenericElementDiv(element, index)
        this.genericElementsContainerTarget.appendChild(elementDiv)
      })
    }
  }

  // Generic Template 요소 생성
  createGenericElementDiv(element, index) {
    const div = document.createElement('div')
    div.className = 'p-0 max-w-none'

    const imageUrl = element.image_url || ''
    const title = element.title || ''
    const subtitle = element.subtitle || ''
    const defaultUrl = (element.default_action && element.default_action.url) ? element.default_action.url : ''

    div.innerHTML = `
      <div class="grid w-full grid-cols-1 lg:grid-cols-2 gap-6 max-w-6xl mx-auto">
        <!-- 왼쪽: 말풍선 미리보기 -->
        <div class="order-2 lg:order-1 min-w-0">
          <div class="sticky top-4 w-full">
            <div class="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-2xl border border-gray-200">
              <div class="text-sm font-medium text-gray-700 flex items-center mb-4">
                <span class="mr-2 text-blue-500">🗨️</span>
                <span>${this.escapeHtml(this.livePreviewLabelValue)}</span>
                <span class="ml-2 text-xs text-gray-500">· ${this.escapeHtml(this.previewChannelLabelValue)}</span>
              </div>

              <!-- 말풍선 -->
              <div class="flex justify-start">
                <div class="max-w-[85%] bg-white border border-gray-200 rounded-2xl shadow-sm p-4" data-preview-card="${index}">
                  <div>
                    <!-- Image 영역 -->
                    <div class="image-preview-${index}">
                      ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(this.genericImageAltValue)}" class="w-full rounded-xl shadow-sm">` : `<div class="w-full h-32 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-sm border-2 border-dashed border-gray-300">${this.escapeHtml(this.genericAddImagePlaceholderValue)}</div>`}
                    </div>

                    <!-- 텍스트 영역 -->
                    <div class="mt-3 space-y-1">
                      <!-- Title -->
                      <div class="title-preview-${index}">
                        ${title ? `<div class="font-semibold text-sm text-gray-900 leading-snug">${this.escapeHtml(title)}</div>` : `<div class="text-sm text-gray-400 font-medium italic">${this.escapeHtml(this.genericTitlePlaceholderValue)}</div>`}
                      </div>

                      <!-- 부Title -->
                      <div class="subtitle-preview-${index}">
                        ${subtitle ? `<div class="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">${this.escapeHtml(subtitle)}</div>` : ''}
                      </div>
                    </div>

                    <!-- 버튼들 -->
                    <div class="buttons-preview-${index}">
                      ${(element.buttons && element.buttons.length > 0) ? `
                        <div class="mt-3 space-y-2">
                          ${element.buttons.map(btn => {
                            const icon = btn.type === 'web_url' ? '🔗' : (btn.type === 'template_call' ? '📄' : (btn.type === 'phone' ? '📞' : '⚙️'))
                            const fallback = btn.type === 'web_url' ? (this.previewDefaultLinkLabelValue) : (btn.type === 'template_call' ? (this.previewDefaultTemplateCallLabelValue) : (btn.type === 'phone' ? this.phoneFieldLabelValue : (this.defaultActionLabelValue)))
                            const label = this.escapeHtml((btn.title && btn.title.trim() !== '') ? btn.title : fallback)
                            return `
                            <div class="w-full text-center py-2 px-3 text-gray-900 text-sm font-medium bg-gray-100 hover:bg-gray-200 cursor-pointer rounded-lg transition-colors">
                              ${icon} ${label}
                            </div>`
                          }).join('')}
                        </div>
                      ` : `<div class="text-[11px] text-gray-400 italic mt-3">${this.escapeHtml(this.genericButtonsEmptyPlaceholderValue)}</div>`}
                    </div>
                  </div>
                </div>
              </div>

              <div class="text-[10px] text-gray-400 text-left mt-2 ml-2 font-mono">Received · 14:32</div>
            </div>
          </div>
        </div>

        <!-- 오른쪽: 입력 폼 -->
        <div class="order-1 lg:order-2 min-w-0">
          <div class="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-lg">
            <div class="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
              <h3 class="text-sm font-medium text-gray-700">${this.escapeHtml(this.genericCardDetailsTitleValue)}</h3>
            </div>

            <div class="p-4 space-y-4">
              <!-- Image 업로드 영역 -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">${this.escapeHtml(this.genericImageLabelValue)}</label>
                <div class="relative group aspect-16/9 min-h-[200px] bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-dashed border-gray-300 rounded-xl hover:border-indigo-400 transition-colors overflow-hidden">
                  ${imageUrl ? `<img src="${imageUrl}" alt="${this.escapeHtml(this.genericImageAltValue)}" class="absolute inset-0 w-full h-full object-cover rounded-xl">` : `
                    <div class='absolute inset-0 flex flex-col items-center justify-center text-gray-500'>
                      <div class="text-4xl mb-3">📷</div>
                      <div class="text-sm font-medium">Drag an image to upload</div>
                      <div class="text-xs text-gray-400 mt-1">or click to select a file</div>
                    </div>
                  `}
                  <div class="absolute inset-0 flex items-center justify-center ${imageUrl ? 'opacity-0 group-hover:opacity-100 bg-black/40' : ''} transition-opacity rounded-xl pointer-events-none">
                    <div class="text-center">
                      <button type="button"
                              class="px-4 py-2 text-sm bg-white text-gray-800 rounded-lg shadow-sm hover:bg-gray-50 pointer-events-auto font-medium"
                              data-action="click->template-wizard#openGenericImagePicker"
                              data-index="${index}">
                        ${imageUrl ? 'Change image' : 'Choose file'}
                      </button>
                    </div>
                    ${imageUrl ? `<button type="button" class="absolute top-3 right-3 px-2 py-1 text-xs bg-red-500 text-white rounded-lg pointer-events-auto hover:bg-red-600 font-medium" data-action="click->template-wizard#removeGenericImage" data-index="${index}">${this.escapeHtml(this.getI18n('removeImageButtonLabel', 'Remove'))}</button>` : ''}
                  </div>
                  <div class="absolute inset-0"
                       data-index="${index}"
                       data-action="click->template-wizard#openGenericImagePicker paste->template-wizard#handleGenericImagePaste dragover->template-wizard#handleGenericImageDragOver dragenter->template-wizard#handleGenericImageDragEnter dragleave->template-wizard#handleGenericImageDragLeave drop->template-wizard#handleGenericImageDrop"></div>
                  <input type="file" accept="image/*" data-template-wizard-target="genericImageInput" data-index="${index}" class="hidden">
                </div>
              </div>

              <!-- 링크 URL -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Image click link <span class="text-gray-400 font-normal">(optional)</span></label>
                <input type="url" value="${this.escapeHtml(defaultUrl)}" placeholder="https://example.com"
                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                       data-action="input->template-wizard#updateGenericElement"
                       data-index="${index}"
                       data-field="default_action_url">
              </div>

              <!-- Title -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">Title</label>
                <input type="text" value="${this.escapeHtml(title)}"
                       maxlength="${this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_TITLE_LENGTH}"
                       placeholder="${this.escapeHtml(this.genericTitlePlaceholderValue)}"
                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                       data-action="input->template-wizard#updateGenericElement"
                       data-index="${index}"
                       data-field="title">
                <div class="flex justify-between text-xs">
                  <span class="text-gray-500">${this.escapeHtml(this.titleHelpTextValue)}</span>
                  <span class="text-gray-400">${(this.textCounterFormatValue).replace('{len}', title.length).replace('{max}', this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_TITLE_LENGTH)}</span>
                </div>
              </div>

              <!-- 부Title -->
              <div class="space-y-2">
                <label class="block text-sm font-medium text-gray-700">${this.escapeHtml(this.subtitleLabelValue)}</label>
                <textarea rows="3" placeholder="${this.escapeHtml(this.subtitlePlaceholderValue)}"
                       maxlength="${this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_SUBTITLE_LENGTH}"
                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
                       data-action="input->template-wizard#updateGenericElement"
                       data-index="${index}"
                       data-field="subtitle">${this.escapeHtml(subtitle)}</textarea>
                <div class="flex justify-between text-xs">
                  <span class="text-gray-500">${this.escapeHtml(this.subtitleHelpTextValue)}</span>
                  <span class="text-gray-400">${(this.textCounterFormatValue).replace('{len}', subtitle.length).replace('{max}', this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_SUBTITLE_LENGTH)}</span>
                </div>
              </div>

              <!-- 버튼 영역 -->
              <div class="space-y-3">
                <label class="block text-sm font-medium text-gray-700">${this.escapeHtml(this.buttonsLabelValue)}</label>
                <div class="flex flex-col space-y-3" data-generic-buttons="${index}">
                  ${this.renderGenericElementButtons(element.buttons || [], index)}
                </div>
                <button type="button"
                        class="w-full px-4 py-2 text-sm text-indigo-600 hover:text-indigo-700 border-2 border-dashed border-indigo-200 rounded-lg hover:border-indigo-300 hover:bg-indigo-50 transition-colors font-medium ${(element.buttons || []).length >= 3 ? 'opacity-50 cursor-not-allowed' : ''}"
                        data-action="click->template-wizard#addGenericElementButton"
                        data-element-index="${index}"
                        ${(element.buttons || []).length >= 3 ? 'disabled' : ''}>
                  <span class="mr-2">+</span>${this.escapeHtml(this.addButtonLabelValue)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `

    return div
  }

  // Generic Template 버튼 렌더링
  renderGenericElementButtons(buttons, elementIndex) {
    if (!buttons || buttons.length === 0) {
      return `<div class=\"text-xs text-gray-500\">${this.escapeHtml(this.noButtonsLabelValue)}</div>`
    }
    
    return buttons.map((button, buttonIndex) => `
      <div class="p-3 bg-white border border-gray-200 rounded-lg space-y-2">
        <div class="flex items-center justify-between">
          <span class="text-sm font-medium text-gray-700">${this.escapeHtml(this.buttonIndexLabelFormatValue.replace('{index}', String(buttonIndex + 1)))}</span>
          <button type="button"
                  class="flex items-center justify-center w-6 h-6 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors"
                  data-action="click->template-wizard#removeGenericElementButton"
                  data-element-index="${elementIndex}"
                  data-button-index="${buttonIndex}"
                  title="${this.escapeHtml(this.removeButtonTitleValue)}">
            🗑️
          </button>
        </div>

        <div class="space-y-2">
          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">${this.escapeHtml(this.buttonTitleLabelValue)}</label>
            <input type="text"
                   value="${this.escapeHtml(button.title || '')}"
                   placeholder="${this.escapeHtml(this.buttonTitlePlaceholderValue)}"
                   maxlength="20"
                   class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                   data-action="input->template-wizard#updateGenericElementButton"
                   data-element-index="${elementIndex}"
                   data-button-index="${buttonIndex}"
                   data-field="title">
          </div>

          <div>
            <label class="block text-xs font-medium text-gray-600 mb-1">${this.escapeHtml(this.buttonTypeLabelValue)}</label>
            <select class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    data-action="change->template-wizard#updateGenericElementButton"
                    data-element-index="${elementIndex}"
                    data-button-index="${buttonIndex}"
                    data-field="type">
              <option value="web_url" ${button.type === 'web_url' ? 'selected' : ''}>🔗 ${this.previewDefaultLinkLabelValue}</option>
              <option value="phone" ${button.type === 'phone' ? 'selected' : ''}>📞 ${this.defaultActionLabelValue}</option>
              <option value="template_call" ${button.type === 'template_call' ? 'selected' : ''}>📋 ${this.previewDefaultTemplateCallLabelValue}</option>
              ${button.type === 'postback' ? '<option value="postback" selected disabled>⚙️ Custom action (deprecated)</option>' : ''}
            </select>
          </div>

          ${(() => {
            if (button.type === 'web_url') {
              const value = this.escapeHtml(button.url || '')
              return `
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">${this.escapeHtml(this.urlFieldLabelValue)}</label>
                <input type="url"
                       value="${value}"
                       placeholder="https://example.com"
                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                       data-action="input->template-wizard#updateGenericElementButton"
                       data-element-index="${elementIndex}"
                       data-button-index="${buttonIndex}"
                       data-field="url">
              </div>`
            } else if (button.type === 'phone') {
              const value = this.escapeHtml(button.display_phone_number || button.phone_number || '')
              return `
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">${this.escapeHtml(this.phoneFieldLabelValue)}</label>
                <input type="tel"
                       value="${value}"
                       placeholder="${this.escapeHtml(this.phonePlaceholderValue)}"
                       class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                       data-action="input->template-wizard#updateGenericElementButton"
                       data-element-index="${elementIndex}"
                       data-button-index="${buttonIndex}"
                       data-field="phone_number">
                <p class="mt-2 text-[11px] text-gray-400">${this.escapeHtml(this.phoneHelpTextValue)}</p>
              </div>`
            } else if (button.type === 'template_call') {
              const selectedId = button.template_id || ''
              return `
              <div>
                <label class="block text-xs font-medium text-gray-600 mb-1">${this.escapeHtml(this.replyTemplateFieldLabelValue)}</label>
                <select class="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        data-action="change->template-wizard#updateGenericElementButton"
                        data-element-index="${elementIndex}"
                        data-button-index="${buttonIndex}"
                        data-field="template_id">
                  <option value="">${this.selectTemplatePlaceholderValue}</option>
                  ${this.getAvailableTemplatesOptions(selectedId)}
                </select>
              </div>`
            } else {
              // postback 등 커스텀 액션은 더 이상 신규 추가를 지원하지 않음
              if (button.type === 'postback') {
                const value = this.escapeHtml(button.payload || '')
                return `
                <div class="space-y-2">
                  <div class="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">${this.postbackNotSupportedMessageValue}</div>
                  <div>
                    <label class="block text-xs font-medium text-gray-600 mb-1">${this.postbackPayloadReadonlyLabelValue}</label>
                    <input type="text" value="${value}" class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50" disabled>
                  </div>
                </div>`
              }
              // 안전망: 기타 타입은 입력 차단
              return ''
            }
          })()}
        </div>
      </div>
    `).join('')
  }

  // Generic Template 요소 업데이트
  updateGenericElement(event) {
    const index = parseInt(event.target.dataset.index)
    const field = event.target.dataset.field
    
    if (field === 'default_action_url') {
      const url = (event.target.value || '').trim()
      if (url.length > 0) {
        this.genericElements[index].default_action = { type: 'web_url', url }
      } else {
        delete this.genericElements[index].default_action
      }
    } else {
      this.genericElements[index][field] = event.target.value
    }
    this.updateGenericElementsField()

    // 실시간 미리보기 업데이트
    this.updatePreviewCard(index)

    // 글자 수 카운터 업데이트
    if (field === 'title' || field === 'subtitle') {
      const counter = event.target.nextElementSibling?.querySelector('.text-gray-400') ||
                     event.target.parentElement.querySelector('.text-gray-400')
      if (counter) {
        const maxLength = field === 'title' ?
          this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_TITLE_LENGTH :
          this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_SUBTITLE_LENGTH
    const fmt = this.textCounterFormatValue
        counter.textContent = fmt.replace('{len}', event.target.value.length).replace('{max}', maxLength)
      }
    }
  }

  // 실시간 미리보기 업데이트
  updatePreviewCard(index) {
    const element = this.genericElements[index]
    if (!element) return

    // Title 업데이트
    const titlePreview = document.querySelector(`.title-preview-${index}`)
    if (titlePreview) {
      const title = element.title || ''
      titlePreview.innerHTML = title ?
        `<div class="font-semibold text-sm text-gray-900 leading-snug">${this.escapeHtml(title)}</div>` :
        `<div class="text-sm text-gray-400 font-medium italic">${this.escapeHtml(this.genericTitlePlaceholderValue)}</div>`
    }

    // 부Title 업데이트
    const subtitlePreview = document.querySelector(`.subtitle-preview-${index}`)
    if (subtitlePreview) {
      const subtitle = element.subtitle || ''
      subtitlePreview.innerHTML = subtitle ?
        `<div class="text-xs text-gray-600 leading-relaxed whitespace-pre-wrap">${this.escapeHtml(subtitle)}</div>` : ''
    }

    // Image 업데이트
    const imagePreview = document.querySelector(`.image-preview-${index}`)
    if (imagePreview) {
      const imageUrl = element.image_url || ''
      imagePreview.innerHTML = imageUrl ?
        `<img src=\"${imageUrl}\" alt=\"${this.escapeHtml(this.genericImageAltValue)}\" class=\"w-full rounded-xl shadow-sm\">` :
        `<div class=\"w-full h-32 bg-gray-100 rounded-xl flex items-center justify-center text-gray-400 text-sm border-2 border-dashed border-gray-300\">${this.escapeHtml(this.genericAddImagePlaceholderValue)}</div>`
    }

    // 버튼 업데이트
    const buttonsPreview = document.querySelector(`.buttons-preview-${index}`)
    if (buttonsPreview) {
      const buttons = element.buttons || []
      buttonsPreview.innerHTML = buttons.length > 0 ? `
        <div class="mt-3 space-y-2">
          ${buttons.map(btn => `
            <div class="w-full text-center py-2 px-3 text-gray-900 text-sm font-medium bg-gray-100 hover:bg-gray-200 cursor-pointer rounded-lg transition-colors">
              ${this.escapeHtml(btn.title || (btn.type === 'web_url' ? (this.previewDefaultLinkLabelValue) : (btn.type === 'template_call' ? (this.previewDefaultTemplateCallLabelValue) : (btn.type === 'phone' ? this.phoneFieldLabelValue : (this.defaultActionLabelValue)))))}
            </div>
          `).join('')}
        </div>
      ` : `<div class=\"text-[11px] text-gray-400 italic mt-3\">${this.escapeHtml(this.genericButtonsEmptyPlaceholderValue)}</div>`
    }
  }

  // Image 업로드 UI: 버튼 클릭 → 숨긴 파일 입력 열기
  openGenericImagePicker(event) {
    const index = parseInt(event.currentTarget.dataset.index)

    // 파일 입력을 더 직접적으로 찾기
    const fileInput = document.querySelector(`input[type="file"][data-index="${index}"]`)

    if (fileInput) {
      fileInput.onchange = (e) => {
        const file = e.target.files && e.target.files[0]
        if (file) {
          this.uploadGenericFile(index, file)
        }
      }
      fileInput.click()
    } else {
      alert(this.fileInputMissingMessageValue)
    }
  }

  // 드래그앤드롭 지원
  handleGenericImageDragOver(event) {
    event.preventDefault()
    const zone = event.currentTarget.parentElement
    if (zone) zone.classList.add('ring-2','ring-indigo-400','ring-offset-2')
  }

  handleGenericImageDrop(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index)
    const zone = event.currentTarget.parentElement
    if (zone) zone.classList.remove('ring-2','ring-indigo-400','ring-offset-2')
    const file = event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files[0]
    if (Number.isInteger(index) && file) {
      this.uploadGenericFile(index, file)
    }
  }

  handleGenericImageDragEnter(event) {
    const zone = event.currentTarget.parentElement
    if (zone) zone.classList.add('ring-2','ring-indigo-400','ring-offset-2')
  }

  handleGenericImageDragLeave(event) {
    const zone = event.currentTarget.parentElement
    if (zone) zone.classList.remove('ring-2','ring-indigo-400','ring-offset-2')
  }

  // 붙여넣기 업로드 지원 (클립보드 Image)
  handleGenericImagePaste(event) {
    const index = parseInt(event.currentTarget.dataset.index)
    const items = (event.clipboardData && event.clipboardData.items) || []
    for (const item of items) {
      if (item.type && item.type.startsWith('image')) {
        const file = item.getAsFile && item.getAsFile()
        if (file && Number.isInteger(index)) {
          event.preventDefault()
          this.uploadGenericFile(index, file)
          break
        }
      }
    }
  }

  // 파일 객체로 업로드 실행
  async uploadGenericFile(index, file) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      const formData = new FormData()
      formData.append('image', file, file.name || 'upload')
      const res = await fetch(buildLocalizedUrl('/uploads/images'), {
        method: 'POST',
        headers: token ? { 'X-CSRF-Token': token } : {},
        body: formData
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.success) {
        alert(data && data.error ? data.error : this.uploadFailedMessageValue)
        return
      }
      let finalUrl = data.url
      if (!finalUrl) { alert(this.uploadUrlMissingMessageValue); return }
      this.genericElements[index].image_url = finalUrl
      this.updateGenericElementsField()
      this.renderGenericElements()
      this.updatePreviewCard(index)
    } catch (e) {
      console.error('Image 업로드 오류:', e)
      alert(this.uploadErrorMessageValue)
    }
  }

  // Generic Template 요소 제거
  removeGenericElement(event) {
    const index = parseInt(event.target.dataset.index)
    this.genericElements.splice(index, 1)
    this.renderGenericElements()
    this.updateGenericElementsField()
    this.updateGenericCardCount()
  }

  // 업로드된 Image 제거
  removeGenericImage(event) {
    event.stopPropagation()
    const index = parseInt(event.currentTarget.dataset.index || event.target.dataset.index)
    if (!Number.isInteger(index)) return
    if (!this.genericElements[index]) return
    delete this.genericElements[index].image_url
    this.updateGenericElementsField()
    this.renderGenericElements()
    this.updatePreviewCard(index)
  }

  // Generic Template 요소의 버튼 추가
  addGenericElementButton(event) {
    const elementIndex = parseInt(event.target.dataset.elementIndex)
    
    if (!this.genericElements[elementIndex].buttons) {
      this.genericElements[elementIndex].buttons = []
    }
    
    if (this.genericElements[elementIndex].buttons.length >= 3) {
      alert(this.genericButtonsMaxMessageValue)
      return
    }
    
    this.genericElements[elementIndex].buttons.push({
      type: 'web_url',
      title: '',
      url: ''
    })
    
    this.renderGenericElements()
    this.updateGenericElementsField()
    this.updatePreviewCard(elementIndex)
  }

  // Generic Template 버튼 업데이트
  updateGenericElementButton(event) {
    const elementIndex = parseInt(event.target.dataset.elementIndex)
    const buttonIndex = parseInt(event.target.dataset.buttonIndex)
    const field = event.target.dataset.field
    
    if (!this.genericElements[elementIndex].buttons[buttonIndex]) {
      this.genericElements[elementIndex].buttons[buttonIndex] = {}
    }
    
    // 타입 변경 시 기존 URL/payload 초기화
    if (field === 'type') {
      const newType = event.target.value
      delete this.genericElements[elementIndex].buttons[buttonIndex].url
      delete this.genericElements[elementIndex].buttons[buttonIndex].payload
      delete this.genericElements[elementIndex].buttons[buttonIndex].template_id
      delete this.genericElements[elementIndex].buttons[buttonIndex].phone_number
      delete this.genericElements[elementIndex].buttons[buttonIndex].display_phone_number
      
      this.genericElements[elementIndex].buttons[buttonIndex].type = newType
      if (newType === 'web_url') {
        this.genericElements[elementIndex].buttons[buttonIndex].url = ''
      } else if (newType === 'template_call') {
        this.genericElements[elementIndex].buttons[buttonIndex].template_id = ''
      } else if (newType === 'phone') {
        this.genericElements[elementIndex].buttons[buttonIndex].phone_number = ''
        this.genericElements[elementIndex].buttons[buttonIndex].display_phone_number = ''
      } else {
        this.genericElements[elementIndex].buttons[buttonIndex].payload = ''
      }
      
      this.renderGenericElements()
    } else {
      this.genericElements[elementIndex].buttons[buttonIndex][field] = event.target.value
      if (field === 'phone_number') {
        this.genericElements[elementIndex].buttons[buttonIndex].display_phone_number = event.target.value
      }
    }

    this.updateGenericElementsField()
    this.updatePreviewCard(elementIndex)
  }

  // Generic Template 버튼 제거
  removeGenericElementButton(event) {
    const elementIndex = parseInt(event.target.dataset.elementIndex)
    const buttonIndex = parseInt(event.target.dataset.buttonIndex)
    
    this.genericElements[elementIndex].buttons.splice(buttonIndex, 1)
    this.renderGenericElements()
    this.updateGenericElementsField()
    this.updatePreviewCard(elementIndex)
  }

  // Generic Template 요소 제안
  suggestGenericElements() {
    const suggestions = [
      {
        title: 'New product',
        subtitle: 'Discover our latest product reflecting current trends',
        image_url: 'https://via.placeholder.com/400x200?text=New+Product',
        buttons: [
          { type: 'web_url', title: 'Learn more', url: 'https://example.com/product' },
          { type: 'postback', title: 'Contact us', payload: 'inquiry_new_product' }
        ]
      },
      {
        title: 'Special offer',
        subtitle: "Limited-time 30% off! Don't miss out",
        image_url: 'https://via.placeholder.com/400x200?text=Special+Discount',
        buttons: [
          { type: 'web_url', title: 'Get discount', url: 'https://example.com/discount' }
        ]
      },
      {
        title: 'Customer reviews',
        subtitle: 'See what our happy customers are saying',
        image_url: 'https://via.placeholder.com/400x200?text=Reviews',
        buttons: [
          { type: 'web_url', title: 'View reviews', url: 'https://example.com/reviews' },
          { type: 'postback', title: 'Write review', payload: 'write_review' }
        ]
      }
    ]
    
    const remaining = this.INSTAGRAM_CONSTRAINTS.GENERIC_TEMPLATE.MAX_ELEMENTS - this.genericElements.length
    const toAdd = Math.min(remaining, suggestions.length)
    
    if (toAdd > 0) {
      for (let i = 0; i < toAdd; i++) {
        this.genericElements.push(suggestions[i])
      }
      this.renderGenericElements()
      this.updateGenericElementsField()
      this.updateGenericCardCount()
      const msg = this.genericSuggestedCardsMessageValue.replace('{count}', String(toAdd))
      alert(msg)
    } else {
      alert(this.genericCardsMaxMessageValue)
    }
  }

  // Generic Template 필드 업데이트
  updateGenericElementsField() {
    if (this.hasGenericElementsFieldTarget) {
      this.genericElementsFieldTarget.value = JSON.stringify(this.genericElements)
    }
  }
}
